import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Button, Image, Input, message, Modal, Spin, Tag } from 'antd';
import { connect, useDispatch } from 'react-redux';
import RestHelper from 'utils/RestHelper';
import { useTranslation } from 'react-i18next';
import { ze } from 'utils/zhEn';
import { useGetState } from 'ahooks';
import scss from './AiDrawer.scss';
import AiChatButtons from 'components/top/AiDrawer/AiChatButtons';
import AiReasoningStatusBlock from 'components/top/AiDrawer/AiReasoningStatusBlock';
import useZuStore from 'store/zustand/zustand-store';
import { ClipboardUtil } from 'utils/clipboard-util';
import { AiDrawerHooks } from 'components/top/AiDrawer/ai-drawer-hooks';
import { ExceptionCategoryUtil } from 'utils/exception-category';
import gfm from 'remark-gfm';
import ReactMarkdown from 'react-markdown';

const markdownComponents = {
  img({ node, ...props}) {
    return <div style={{ maxWidth:'100%' }}>
      <Image src={props.src} />
    </div>
  },
};

const AiStackAnalysisDrawerBody = (props) => {
  const {
    reduxState,
    trigger,
  } = props;

  const aiDrawerSlice = useZuStore().aiDrawer;

  const { t } = useTranslation();
  const [abortController, setAbortController] = useState(null);
  // 使用 ref 保存 abortController 的引用，确保在组件卸载时能拿到最新的值
  const abortControllerRef = useRef(null);
  const [aiResult, setAiResult] = useState('');
  const [aiResultId, setAiResultId] = useState('');

  // AI reasoning 相关状态
  const [isAiReasoning, setIsAiReasoning, getIsAiReasoning] = useGetState(false);
  const [aiReasoningContent, setAiReasoningContent] = useState('');
  const [isReasonCollapsed, setIsReasonCollapsed] = useState(false);

  const currentApp = reduxState.app.get('current').toJS();
  const { appId, pid } = currentApp;
  const issueObj = reduxState.issue.toJS();
  const { crashDoc } = issueObj.current || {};

  const { isLike, isDislike, onClickLikeOrDislike } = AiDrawerHooks.useLikeDislike(appId, aiResultId);

  // 同步 abortController 到 ref
  useEffect(() => {
    abortControllerRef.current = abortController;
  }, [abortController]);

  // 组件卸载时中断请求（切换到不同堆栈时）
  useEffect(() => {
    return () => {
      // 使用 ref 获取最新的 abortController 并中断
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  useEffect(() => {
    if (!trigger) {
      return;
    }
    startAiStackAnalysis();
  }, [trigger]);

  function onEventSourceMessage(rawData) {
    const REASONING_START = 'Reasoning Content Start!';
    const REASONING_END = 'Reasoning Content End!';

    let data = rawData;
    try {
      data = JSON.parse(rawData);
    } catch (e) {
      console.error(`AI stack analysis error when parsing: `, rawData);
    }

    if (data === REASONING_START) {
      setIsAiReasoning(true);
      setAiReasoningContent('');
    } else if (data === REASONING_END) {
      setIsAiReasoning(false);
    } else if (data.includes('AiAnalyseResult')){
      const pattern = /\d+/;
      const match = pattern.exec(data);
      const id = match[0];
      setAiResultId(id);
    } else if (data) {
      const cb = (oldValue => {
        if (!!data.trim() || !!oldValue) {
          return oldValue + data;
        } else {
          return oldValue;
        }
      });
      if (getIsAiReasoning()) {
        setAiReasoningContent(cb);
      } else {
        setAiResult(cb);
      }
    }
  }

  async function startAiStackAnalysis(clearCache = false) {
    // 如果存在之前的请求，先取消
    if (abortController) {
      abortController.abort();
      setAbortController(null);
    }

    // 重置所有状态
    setAiResultId('');
    setAiResult('');
    setAiReasoningContent('');
    setIsAiReasoning(false);

    const crashMap = crashDoc.crashMap || {};
    const isJankCategory = ExceptionCategoryUtil.isJank(ExceptionCategoryUtil.fromExceptionTypeInt(crashMap.crashExceptionType));

    const params = {
      appId,
      crashId: crashMap.crashId,
      expThread: crashMap.threadName || '',
      expName: crashMap.expName,
      expAddr: crashMap.expAddr || '',
      stack: crashMap.retraceCrashDetail,
      stream: true,
      clearCache,
    };
    const url = isJankCategory ? '/api/aiPilot/analysisStutter' : '/api/aiPilot/streamAnalyse';

    // 创建新的 AbortController
    const ac = new AbortController();
    setAbortController(ac);

    try {
      await RestHelper.postEventStream(url, params, {
        signal: ac.signal,
        statusCodeToCallback: {
          500() {
            message.error(ze('后台发生错误，请稍后再试','Server error, please try again later'));
          },
        },
        onmessage:(event) => {
          const rawData = event.data;
          if (!rawData) {
            return;
          }
          onEventSourceMessage(rawData);
        },
      });
    } catch (e) {
      if (e.name === 'AbortError') {
        console.log('Request was aborted');
      } else {
        message.error(e);
      }
    } finally {
      if (abortController === ac) {
        setAbortController(null);
      }
    }
  }

  async function onClickCopy() {
    await ClipboardUtil.copyToClipboard(aiResult);
    message.success(ze('已复制至剪贴板', 'Copied to clipboard.'));
  }

  return <div
    className={scss.issueAiAnalysisDrawerBody}
  >
    <div>
      { aiReasoningContent && <div>
        <AiReasoningStatusBlock
          isReasoning={isAiReasoning}
          isCollapsed={isReasonCollapsed}
          onChangeCollapsed={(v) => setIsReasonCollapsed(v)}
        />
        { !isReasonCollapsed && <pre className={scss.reasoningContent}>
          <div>{ aiReasoningContent }</div>
        </pre> }
      </div> }
      <div
        style={{ marginBottom: '24px' }}
      ><ReactMarkdown
        remarkPlugins={[gfm]}
        linkTarget='_blank'
        children={aiResult}
        components={markdownComponents}
      /></div>
    </div>
    { aiResultId && <AiChatButtons
      showLikeButtons={true}
      isLikeSelected={isLike}
      isDislikeSelected={isDislike}
      onLike={() => onClickLikeOrDislike(true)}
      onDislike={() => onClickLikeOrDislike(false)}
      onCopy={() => onClickCopy()}
      onRefresh={() => startAiStackAnalysis(true)}
    /> }
  </div>;
};


const mapStateToProps = (state, router) => ({
  reduxState: state,
});

export default connect(mapStateToProps)(AiStackAnalysisDrawerBody);
